import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

interface CategoryDropdownProps {
  variant?: 'nav' | 'hero';
  label?: string;
}

export default function CategoryDropdown({ variant = 'hero', label }: CategoryDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const buttonClass =
    variant === 'nav'
      ? 'flex items-center gap-1 whitespace-nowrap text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer'
      : 'flex w-full items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-left text-sm font-medium text-foreground-900 transition-colors hover:border-primary-300 cursor-pointer';

  const panelClass =
    variant === 'nav'
      ? 'absolute left-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-lg'
      : 'absolute left-0 right-0 top-full z-50 mt-2 max-h-80 w-full overflow-y-auto rounded-lg border border-background-200 bg-background-50 shadow-lg';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={buttonClass}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{label || t('home.hero.dropdownPlaceholder')}</span>
        <i
          className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className={panelClass} role="listbox">
          {categories.map((c) => (
            <a
              key={c.key}
              href={c.url}
              target="_blank"
              rel="noopener nofollow"
              role="option"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-foreground-950"
            >
              <span className="truncate">{t(`categories.${c.key}`)}</span>
              <i className="ri-arrow-right-up-line text-sm text-foreground-300" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}